import React, { useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/api/client";
import { Panel, ActionButton, TextField } from "./_shared";
import { Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";

export default function PropertiesManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/properties");
    setItems(data || []);
  };
  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({ slug: "", name: "", location: "", lat: null, lng: null, is_default: false });

  const save = async () => {
    try {
      if (editing.id) {
        await api.patch(`/admin/properties/${editing.id}`, editing);
      } else {
        await api.post("/admin/properties", editing);
      }
      toast.success("Salvato");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const remove = async (id) => {
    if (!confirm("Eliminare questa struttura? Gli appartamenti e categorie legati resteranno finché non ricollegati.")) return;
    await api.delete(`/admin/properties/${id}`);
    load();
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-editorial italic text-4xl text-ink-900">Strutture</h1>
          <p className="text-xs text-ink-500 mt-1">Multi-struttura pronto per il futuro</p>
        </div>
        <ActionButton onClick={startNew} testid="properties-add"><Plus className="w-3.5 h-3.5" />Nuova struttura</ActionButton>
      </div>

      <Panel title="Elenco" testid="properties-list">
        <div className="divide-y divide-ink-200/30">
          {items.map((p) => (
            <div key={p.id} className="py-3 flex items-center justify-between" data-testid={`property-row-${p.id}`}>
              <div>
                <div className="font-semibold text-ink-900">{p.name} {p.is_default && <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-corten-50 text-corten-500 uppercase tracking-wider">Default</span>}</div>
                <div className="text-xs text-ink-500">{p.location}</div>
              </div>
              <div className="flex gap-2">
                <ActionButton variant="secondary" testid={`property-edit-${p.id}`} onClick={() => setEditing(p)}>Modifica</ActionButton>
                {!p.is_default && <ActionButton variant="danger" testid={`property-delete-${p.id}`} onClick={() => remove(p.id)}><Trash2 className="w-3.5 h-3.5" /></ActionButton>}
              </div>
            </div>
          ))}
        </div>
      </Panel>

      {editing && (
        <Panel title={editing.id ? "Modifica struttura" : "Nuova struttura"} testid="property-editor" action={
          <div className="flex gap-2">
            <ActionButton variant="secondary" onClick={() => setEditing(null)}>Annulla</ActionButton>
            <ActionButton onClick={save} testid="property-save"><Save className="w-3.5 h-3.5" />Salva</ActionButton>
          </div>
        }>
          <div className="grid md:grid-cols-2 gap-4">
            <TextField label="Nome" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} testid="property-name" />
            <TextField label="Slug (URL)" value={editing.slug} onChange={(v) => setEditing({ ...editing, slug: v })} testid="property-slug" hint="es. la-piazzetta"/>
            <TextField label="Località" value={editing.location} onChange={(v) => setEditing({ ...editing, location: v })} testid="property-location" />
            <TextField label="Latitudine" type="number" value={editing.lat} onChange={(v) => setEditing({ ...editing, lat: v })} testid="property-lat" />
            <TextField label="Longitudine" type="number" value={editing.lng} onChange={(v) => setEditing({ ...editing, lng: v })} testid="property-lng" />
            <label className="flex items-center gap-2 mt-4">
              <input type="checkbox" checked={!!editing.is_default} onChange={(e) => setEditing({ ...editing, is_default: e.target.checked })} data-testid="property-is-default" />
              <span className="text-sm">Struttura predefinita</span>
            </label>
          </div>
        </Panel>
      )}
    </div>
  );
}
